import { MapContainer, TileLayer, Polygon, FeatureGroup, useMap } from "react-leaflet";
import L from "leaflet";
import React, { useEffect } from "react";
import { MAP_TILE_CONFIG } from "@racoon/lib";

// Mock geo data
export const MOCK_GEO_DATA = {
  center: [43.238949, 76.940088] as [number, number], // Almaty, Kazakhstan
  boundaryCoordinates: [
    [43.2408, 76.9351],
    [43.2408, 76.9436],
    [43.2348, 76.9436],
    [43.2348, 76.9351],
    [43.2408, 76.9351],
  ] as [number, number][],
};

// Component to handle map view updates
const MapViewUpdater = ({ center }: { center: [number, number] }) => {
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      // Smoothly animate to new center
      map.flyTo(center, map.getZoom(), { duration: 0.5 });
    }
  }, [center, map]);

  return null;
};

// Component to fit map bounds to polygon coordinates
const BoundsUpdater = ({ coordinates }: { coordinates: [number, number][] }) => {
  const map = useMap();

  useEffect(() => {
    if (map && coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [coordinates, map]);

  return null;
};

// Draw control component
const DrawControl = ({
  isEditingGeofence,
  onGeofenceUpdate,
  featureGroupRef,
}: {
  isEditingGeofence: boolean;
  onGeofenceUpdate: (coordinates: [number, number][]) => void;
  featureGroupRef: React.RefObject<L.FeatureGroup>;
}) => {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !featureGroupRef.current) return;

    if (isEditingGeofence) {
      // Ensure the feature group is on the map
      if (!map.hasLayer(featureGroupRef.current)) {
        map.addLayer(featureGroupRef.current);
      }

      const drawControl = new (L.Control as any).Draw({
        position: "topright",
        edit: {
          featureGroup: featureGroupRef.current,
        },
        draw: {
          polygon: true,
          rectangle: false,
          circle: false,
          circlemarker: false,
          marker: false,
          polyline: false,
        },
      });

      map.addControl(drawControl);

      // Listen to draw events
      map.on("draw:created", (e: any) => {
        const layer = e.layer;
        featureGroupRef.current?.addLayer(layer);
        const coordinates = layer
          .getLatLngs()[0]
          .map((latlng: any) => [latlng.lat, latlng.lng] as [number, number]);
        onGeofenceUpdate(coordinates);
      });

      map.on("draw:edited", (e: any) => {
        e.layers.eachLayer((layer: any) => {
          const coordinates = layer
            .getLatLngs()[0]
            .map((latlng: any) => [latlng.lat, latlng.lng] as [number, number]);
          onGeofenceUpdate(coordinates);
        });
      });

      map.on("draw:deleted", () => {
        // Get remaining layers in the feature group
        const layers = featureGroupRef.current?.getLayers() || [];

        if (layers.length === 0) {
          onGeofenceUpdate([]);
        } else {
          // Update with remaining layers
          layers.forEach((layer: any) => {
            if (layer.getLatLngs) {
              const coordinates = layer
                .getLatLngs()[0]
                .map((latlng: any) => [latlng.lat, latlng.lng] as [number, number]);
              onGeofenceUpdate(coordinates);
            }
          });
        }
      });

      return () => {
        map.removeControl(drawControl);
        map.off("draw:created");
        map.off("draw:edited");
        map.off("draw:deleted");
      };
    }
  }, [map, isEditingGeofence, onGeofenceUpdate, featureGroupRef]);

  return null;
};

interface SuspiciousSiteMapProps {
  isEditingGeofence: boolean;
  boundaryCoordinates: [number, number][];
  onGeofenceUpdate: (coordinates: [number, number][]) => void;
  featureGroupRef: React.RefObject<L.FeatureGroup>;
  center?: [number, number]; // Optional center for map viewport
}

export const SuspiciousSiteMap: React.FC<SuspiciousSiteMapProps> = ({
  isEditingGeofence,
  boundaryCoordinates,
  onGeofenceUpdate,
  featureGroupRef,
  center = MOCK_GEO_DATA.center,
}) => {
  return (
    <MapContainer
      center={center}
      zoom={13}
      style={{ height: "100%", width: "100%", borderRadius: "8px" }}
      className="rounded-[8px]"
      zoomControl={false}
    >
      <TileLayer
        url={MAP_TILE_CONFIG.url}
        subdomains={MAP_TILE_CONFIG.subdomains}
        attribution={MAP_TILE_CONFIG.attribution}
      />
      <MapViewUpdater center={center} />
      <BoundsUpdater coordinates={boundaryCoordinates} />
      <FeatureGroup ref={featureGroupRef}>
        {boundaryCoordinates.length > 0 && (
          <Polygon
            positions={boundaryCoordinates}
            pathOptions={{
              color: "#DC2626",
              fill: true,
              fillColor: "#FCA5A5",
              fillOpacity: 0.5,
              weight: 2,
            }}
          />
        )}
      </FeatureGroup>
      <DrawControl
        isEditingGeofence={isEditingGeofence}
        onGeofenceUpdate={onGeofenceUpdate}
        featureGroupRef={featureGroupRef}
      />
    </MapContainer>
  );
};
